import type { Employee, PayRecord } from '../types';

/** Optional data not stored on `Employee` (list API) for report cells. */
export interface EmployeeReportCellContext {
  activePayRecord?: PayRecord;
}

export interface EmployeeReportOutputFieldDef {
  key: string;
  defaultLabel: string;
}

/**
 * Output columns available on Reports → Employee Details.
 * Keys align with `buildEmployeeFilterFields` in employeeAdvancedFilters.ts
 * (except `employeeReportStatus`, which is report scope only, not a row attribute).
 */
export const EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG: EmployeeReportOutputFieldDef[] = [
  { key: 'id', defaultLabel: 'Employee ID' },
  { key: 'fullName', defaultLabel: 'Full Name' },
  { key: 'email', defaultLabel: 'Email' },
  { key: 'pin', defaultLabel: 'PIN' },
  { key: 'title', defaultLabel: 'Title' },
  { key: 'firstName', defaultLabel: 'First Name' },
  { key: 'lastName', defaultLabel: 'Last Name' },
  { key: 'gender', defaultLabel: 'Gender' },
  { key: 'genderOther', defaultLabel: 'Gender (Other)' },
  { key: 'birthday', defaultLabel: 'Birthday' },
  { key: 'phone', defaultLabel: 'Phone' },
  { key: 'address', defaultLabel: 'Address (legacy)' },
  { key: 'address1', defaultLabel: 'Address line 1' },
  { key: 'address2', defaultLabel: 'Address line 2' },
  { key: 'address3', defaultLabel: 'Address line 3' },
  { key: 'county', defaultLabel: 'County' },
  { key: 'zipCode', defaultLabel: 'Zip code' },
  { key: 'psaLicense', defaultLabel: 'PSA License' },
  { key: 'startDate', defaultLabel: 'Start Date' },
  { key: 'visaNumber', defaultLabel: 'Visa Number' },
  { key: 'visaExpiryDate', defaultLabel: 'Visa Expiry Date' },
  { key: 'comment', defaultLabel: 'Comment' },
  { key: 'mainLocationId', defaultLabel: 'Main Location ID' },
  { key: 'mainLocation', defaultLabel: 'Main Location' },
  { key: 'mainOrSubLocationIds', defaultLabel: 'Main or Sub Location IDs' },
  { key: 'mainPositionId', defaultLabel: 'Main Position ID' },
  { key: 'mainPosition', defaultLabel: 'Main Position' },
  { key: 'mainOrSubPositionIds', defaultLabel: 'Main or Sub Position IDs' },
  { key: 'scheduleIds', defaultLabel: 'Schedule IDs' },
  { key: 'appRoleIds', defaultLabel: 'App Role IDs' },
  { key: 'payrollDepartmentId', defaultLabel: 'Payroll Department ID' },
  { key: 'payrollDepartment', defaultLabel: 'Payroll Department' },
  { key: 'employmentType', defaultLabel: 'Employment Type' },
  { key: 'allowMobileClockIn', defaultLabel: 'Allow Mobile Clock In' },
  { key: 'leavePolicyId', defaultLabel: 'Assigned Leave Policy' },
  { key: 'licenseNumber', defaultLabel: 'License - Number' },
  { key: 'licenseExpiryDate', defaultLabel: 'License - Expiry Date' },
  { key: 'worksNumber', defaultLabel: 'Works Number' },
  { key: 'ppsNumber', defaultLabel: 'PPS Number' },
  { key: 'accountNumber', defaultLabel: 'Bank Account Number' },
  { key: 'accountName', defaultLabel: 'Bank Account Name' },
  { key: 'bankName', defaultLabel: 'Bank Name' },
  { key: 'ban', defaultLabel: 'BAN' },
  { key: 'bic', defaultLabel: 'BIC' },
  { key: 'sortCode', defaultLabel: 'Sort Code' },
  { key: 'payRecordPaymentTypeId', defaultLabel: 'Pay Type (active pay record ID)' },
  { key: 'payRecordPaymentFrequencyId', defaultLabel: 'Pay Frequency (active pay record ID)' },
  { key: 'payRecordContractedHours', defaultLabel: 'Contracted Hours (Per Week)' },
  { key: 'payRecordBaseRate', defaultLabel: 'Pay Rate' },
  { key: 'payRecordSundayRateMultiplier', defaultLabel: 'Sunday Rate Multiplier' },
  { key: 'payRecordBankHolidayRateMultiplier', defaultLabel: 'Bank Holiday Rate Multiplier' },
  { key: 'payRecordNightShiftMultiplier', defaultLabel: 'Night Shift Multiplier' },
  { key: 'payRecordOvertimeEligible', defaultLabel: 'Is Overtime Eligible' },
  { key: 'payRecordOt1TriggerHours', defaultLabel: 'OT1 Trigger Hours' },
  { key: 'payRecordOt1RateMultiplier', defaultLabel: 'OT1 Rate Multiplier' },
  { key: 'payRecordOt2TriggerHours', defaultLabel: 'OT2 Trigger Hours' },
  { key: 'payRecordOt2RateMultiplier', defaultLabel: 'OT2 Rate Multiplier' },
  { key: 'feedbackType', defaultLabel: 'Feedback - Type' },
  { key: 'feedbackActionTaken', defaultLabel: 'Feedback - Action Taken' },
  { key: 'feedbackDescription', defaultLabel: 'Feedback - Description' },
  { key: 'feedbackPriority', defaultLabel: 'Feedback - Priority' },
  { key: 'feedbackStatus', defaultLabel: 'Feedback - Status' },
  { key: 'trainingUnassigned', defaultLabel: 'Training - Unassigned' },
  { key: 'trainingPending', defaultLabel: 'Training - Pending' },
  { key: 'trainingCompleted', defaultLabel: 'Training - Completed' },
  { key: 'notesSubject', defaultLabel: 'Notes - Subject' },
  { key: 'notesContent', defaultLabel: 'Notes - Description' },
  { key: 'isActive', defaultLabel: 'Status' },
];

export function getDefaultLabelForOutputField(fieldKey: string): string {
  return EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG.find((f) => f.key === fieldKey)?.defaultLabel || fieldKey;
}

function joinIds(ids?: number[] | null): string {
  if (!ids?.length) return '';
  return ids.map(String).join(', ');
}

function mainOrSubLocationIdsCell(employee: Employee): string {
  const p = employee.profile;
  if (!p?.mainLocationId) return joinIds(p?.subLocationIds);
  const set = new Set<number>([p.mainLocationId, ...(p.subLocationIds || [])]);
  return Array.from(set).join(', ');
}

function mainOrSubPositionIdsCell(employee: Employee): string {
  const p = employee.profile;
  if (!p?.mainPositionId) return joinIds(p?.subPositionIds);
  const set = new Set<number>([p.mainPositionId, ...(p.subPositionIds || [])]);
  return Array.from(set).join(', ');
}

function boolYesNo(v: boolean | undefined | null): string {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '';
}

function reportNum(v: number | string | undefined | null): string {
  if (v == null || v === '') return '';
  if (typeof v === 'number') return Number.isNaN(v) ? '' : String(v);
  const n = Number(v);
  return Number.isNaN(n) ? '' : String(n);
}

export function formatEmployeeReportCell(
  employee: Employee,
  fieldKey: string,
  context?: EmployeeReportCellContext
): string {
  const p = employee.profile;
  const pr = employee.payroll;
  const payRec = context?.activePayRecord;
  const trimmedKey = String(fieldKey || '').trim();
  const key =
    EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG.find(
      (f) => f.key.toLowerCase() === trimmedKey.toLowerCase()
    )?.key ?? trimmedKey;

  switch (key) {
    case 'id':
      return String(employee.id);
    case 'firstName':
      return employee.firstName || '';
    case 'lastName':
      return employee.lastName || '';
    case 'fullName':
      return `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
    case 'email':
      return employee.email || '';
    case 'pin':
      return employee.pin || '';
    case 'title':
      return employee.title || '';
    case 'phone':
      return employee.phone || employee.phoneNumber || '';
    case 'gender':
      return employee.gender || '';
    case 'genderOther':
      return employee.genderOther || '';
    case 'birthday':
      return employee.birthday || employee.dateOfBirth || '';
    case 'startDate':
      return employee.startDate || employee.hireDate || '';
    case 'address':
      return employee.address || '';
    case 'address1':
      return employee.address1 || '';
    case 'address2':
      return employee.address2 || '';
    case 'address3':
      return employee.address3 || '';
    case 'county':
      return employee.county || '';
    case 'zipCode':
      return employee.zipCode || '';
    case 'psaLicense':
      return employee.psaLicense || '';
    case 'visaNumber':
      return employee.visaNumber || '';
    case 'visaExpiryDate':
      return employee.visaExpiryDate || '';
    case 'comment':
      return employee.comment || '';
    case 'mainLocation':
      return p?.mainLocation?.name || p?.locationName || '';
    case 'mainLocationId':
      return p?.mainLocationId != null ? String(p.mainLocationId) : '';
    case 'mainOrSubLocationIds':
      return mainOrSubLocationIdsCell(employee);
    case 'mainPosition':
      return p?.mainPosition?.title || '';
    case 'mainPositionId':
      return p?.mainPositionId != null ? String(p.mainPositionId) : '';
    case 'mainOrSubPositionIds':
      return mainOrSubPositionIdsCell(employee);
    case 'scheduleIds':
      return joinIds(p?.scheduleIds);
    case 'appRoleIds':
      return joinIds(p?.appRoleIds);
    case 'payrollDepartment':
      return p?.payrollDepartment?.name || '';
    case 'payrollDepartmentId':
      return p?.payrollDepartmentId != null ? String(p.payrollDepartmentId) : '';
    case 'employmentType':
      return p?.employmentType || '';
    case 'allowMobileClockIn':
      return boolYesNo(p?.allowMobileClockIn);
    case 'leavePolicyId':
      return (
        employee.leavePolicy?.policyName ||
        (employee.leavePolicyId != null ? String(employee.leavePolicyId) : '')
      );
    case 'isActive':
      return employee.isActive ? 'Active' : 'Inactive';
    case 'worksNumber':
      return pr?.worksNumber || '';
    case 'ppsNumber':
      return pr?.ppsNumber || '';
    case 'accountNumber':
      return pr?.accountNumber || '';
    case 'accountName':
      return pr?.accountName || '';
    case 'bankName':
      return pr?.bankName || '';
    case 'ban':
      return pr?.ban || '';
    case 'bic':
      return pr?.bic || '';
    case 'sortCode':
      return pr?.sortCode || '';
    case 'licenseNumber':
    case 'licenseExpiryDate':
      return '';
    case 'payRecordPaymentTypeId':
      return payRec?.paymentTypeId != null ? String(payRec.paymentTypeId) : '';
    case 'payRecordPaymentFrequencyId':
      return payRec?.paymentFrequencyId != null ? String(payRec.paymentFrequencyId) : '';
    case 'payRecordContractedHours':
      return reportNum(payRec?.contractedHours);
    case 'payRecordBaseRate':
      if (payRec?.baseRate != null) return reportNum(payRec.baseRate);
      return reportNum(pr?.hourlyRate);
    case 'payRecordSundayRateMultiplier':
      return reportNum(payRec?.sundayRateMultiplier);
    case 'payRecordBankHolidayRateMultiplier':
      return reportNum(payRec?.bankHolidayRateMultiplier);
    case 'payRecordNightShiftMultiplier':
      return reportNum(payRec?.nightShiftMultiplier);
    case 'payRecordOvertimeEligible':
      return boolYesNo(payRec?.overtimeEligible);
    case 'payRecordOt1TriggerHours':
      return reportNum(payRec?.ot1TriggerHours);
    case 'payRecordOt1RateMultiplier':
      return reportNum(payRec?.ot1RateMultiplier);
    case 'payRecordOt2TriggerHours':
      return reportNum(payRec?.ot2TriggerHours);
    case 'payRecordOt2RateMultiplier':
      return reportNum(payRec?.ot2RateMultiplier);
    case 'feedbackType':
    case 'feedbackActionTaken':
    case 'feedbackDescription':
    case 'feedbackPriority':
    case 'feedbackStatus':
    case 'trainingUnassigned':
    case 'trainingPending':
    case 'trainingCompleted':
      return '';
    case 'notesSubject':
      return p?.notesSubject || '';
    case 'notesContent':
      return p?.notesContent || '';
    default:
      return '';
  }
}

export const DEFAULT_EMPLOYEE_REPORT_OUTPUT_KEYS = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'mainLocation',
  'mainPosition',
  'employmentType',
  'isActive',
];
